/** Hooks des Vertragstext-Editors (`/api/pflege/vertragstext`).
 *
 *  Der Vertragstext kommt aus `vertrag_klauselwerk` / `vertrag_klausel` statt
 *  aus einer Jinja-Vorlage im Repo. Konzept: docs/KONZEPT-VERTRAGSEDITOR.md.
 *
 *  Zwei Dinge, die diese Datei durchgängig annimmt:
 *
 *  - **Nummern kommen nie vom Client.** Weder `§ 7` noch `(3)`. Der Server
 *    vergibt sie beim Rendern aus der Reihenfolge; die Oberfläche zeigt sie
 *    nur an. Wer hier eine Nummer schickt, baut die zweite Wahrheit ein, die
 *    das ganze Vorhaben vermeiden soll.
 *  - **Nach jedem Schreiben wird die Fassung neu geladen.** Sortierung,
 *    Absatznummern und Querverweise hängen voneinander ab — eine lokal
 *    zusammengeflickte Antwort wäre irgendwann falsch, ohne dass es auffällt.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost } from "@vimaana/ui";

const BASE = "/api/pflege/vertragstext";

// `fehlertext` wohnt in lib/fehler.ts — die Meldung wird ueberall gebraucht,
// wo geschrieben wird, nicht nur im Vertragstext-Editor. Hier nur noch
// weitergereicht, damit die bestehenden Importe gueltig bleiben.
export { fehlertext } from "../lib/fehler";

/** Auf welches Blatt des Dokumentensatzes eine Klausel gedruckt wird.
 *  Migration 115 — Konzept: docs/KONZEPT-VERTRAG-DREI-SEITEN-UND-AGB.md. */
export type Blatt = "urkunde" | "anlage" | "mandat" | "agb" | "bestand";

export type Klausel = {
  id: string;
  werk_id: string;
  dokument: Blatt;
  paragraf: number;
  paragraf_titel: string;
  paragraf_stil: string | null;
  sortierung: number;
  art: "absatz" | "baustein";
  text: string;
  baustein: string | null;
  bedingung: Record<string, string | boolean> | null;
  schluessel: string | null;
  stil: string | null;
  anhang: string | null;
};

export type Fassung = {
  id: string;
  version: number;
  status: "entwurf" | "freigegeben" | "abgeloest";
  gilt_ab: string | null;
  notiz: string;
  freigegeben_am: string | null;
  freigegeben_von: string | null;
  absaetze: number;
  bausteine: number;
  /** Wie viele Verträge auf dieser Fassung festgeschrieben sind. */
  vertraege: number;
  /** Entwurf — oder freigegeben, aber noch kein Kunde hat zugestimmt
   *  (28.09.2026). Entscheidet der Server, nicht die Oberfläche. */
  bearbeitbar?: boolean;
};

export type FassungDetail = Fassung & { klauseln: Klausel[] };

export type BlattInfo = {
  name: Blatt;
  titel: string;
  /** „Ziffer" in der Urkunde, „§" in den AGB, null wo nicht nummeriert wird. */
  zaehlung: string | null;
  /** Nur die Urkunde hat eine Obergrenze — sie ist der Grund des Umbaus. */
  seitenbudget: number | null;
  hinweis: string;
};

export type Werkzeugkasten = {
  platzhalter: { name: string; beispiel: string }[];
  schalter: string[];
  bausteine: { name: string; titel: string }[];
  blaetter: BlattInfo[];
  doppelte_zahlen: { muster: string; hinweis: string }[];
};

export type Vorschau = {
  paragrafen_html: string;
  anhaenge_html: string;
  agb_html: string;
  anlage_html: string;
  mandat_html: string;
  bestand_html: string;
  css: string;
  schalter: Record<string, string | boolean>;
  /** Gemessene Seitenzahl je Blatt — leer, wenn sich das PDF nicht bauen ließ. */
  seiten: Partial<Record<"urkunde" | "anlage" | "mandat" | "agb" | "widerruf" | "bestand", number>>;
  /** Die Blätter als Seitenbilder aus dem echten PDF — mit Briefkopf und
   *  Fußzeile (seit 26.10.2026). Leer, wenn das Rendern scheiterte. */
  seiten_bilder?: Partial<Record<"urkunde" | "anlage" | "mandat" | "agb" | "widerruf" | "bestand", string[]>>;
};

export const STATUS_LABEL: Record<string, string> = {
  entwurf: "Entwurf",
  freigegeben: "Freigegeben",
  abgeloest: "Abgelöst",
};

/** Klartext für die Schalter — im Editor steht nicht `hat_zugang_notiz`. */
export const SCHALTER_LABEL: Record<string, string> = {
  vertragsart: "Vertragsart",
  zahlungsart: "Zahlungsart",
  zahlweise_offen: "Zahlweise noch offen",
  lastschrift_wirksam: "Lastschrift steht fest",
  hat_rate: "Es gibt eine Rate",
  hat_zugang_notiz: "Zugangshinweis vorhanden",
  hat_sondervereinbarungen: "Sondervereinbarung vorhanden",
  hat_objekt_positionen: "Bestandsaufnahme vorhanden",
  hat_optionale: "Abrufleistungen vorhanden",
  hat_zusatzsaetze: "Zusatzsätze vorhanden",
};

/** Die Werte, die ein Schalter annehmen kann. Alles außer den beiden
 *  Textschaltern ist ja/nein — deshalb steht hier nur, was davon abweicht. */
export const SCHALTER_WERTE: Record<string, string[]> = {
  vertragsart: ["verbraucher", "unternehmer"],
  zahlungsart: ["lastschrift", "ueberweisung"],
};

const SCHLUESSEL = ["pflege", "vertragstext"] as const;

export function useFassungen() {
  return useQuery<{ fassungen: Fassung[]; aktuelle_id: string | null; vorlage_ist_quelle: boolean }>({
    queryKey: [...SCHLUESSEL, "fassungen"],
    queryFn: () => apiGet(`${BASE}/fassungen`),
    staleTime: 10_000,
  });
}

export function useFassung(werkId: string | null) {
  return useQuery<FassungDetail>({
    queryKey: [...SCHLUESSEL, "fassung", werkId],
    queryFn: () => apiGet(`${BASE}/fassungen/${werkId}`),
    enabled: !!werkId,
  });
}

export function useWerkzeugkasten() {
  return useQuery<Werkzeugkasten>({
    queryKey: [...SCHLUESSEL, "bausteine"],
    queryFn: () => apiGet(`${BASE}/bausteine`),
    // Ändert sich nur mit einem Deploy — nicht bei jedem Seitenwechsel neu holen.
    staleTime: 60 * 60_000,
  });
}

/** Alles, was eine Fassung verändert, geht hierdurch: eine Stelle, an der
 *  invalidiert wird, statt zwölf, von denen eine vergessen wird. */
function useSchreiben<TVars>(fn: (v: TVars) => Promise<unknown>, werkId?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...SCHLUESSEL, "fassungen"] });
      if (werkId) qc.invalidateQueries({ queryKey: [...SCHLUESSEL, "fassung", werkId] });
    },
  });
}

export function useFassungAnlegen() {
  return useSchreiben<{ quelle_id?: string | null; notiz?: string }>(
    (b) => apiPost(`${BASE}/fassungen`, b),
  );
}

export function useFassungAusVorlage() {
  return useSchreiben<void>(() => apiPost(`${BASE}/fassungen/aus-vorlage`, {}));
}

export function useFassungVerwerfen() {
  return useSchreiben<string>((id) => apiDelete(`${BASE}/fassungen/${id}`));
}

export function useFreigeben(werkId: string | null) {
  return useSchreiben<{ gilt_ab: string }>(
    (b) => apiPost(`${BASE}/fassungen/${werkId}/freigeben`, b), werkId);
}

export function useKlauselAnlegen(werkId: string | null) {
  return useSchreiben<Partial<Klausel>>(
    (b) => apiPost(`${BASE}/fassungen/${werkId}/klauseln`, b), werkId);
}

export function useKlauselAendern(werkId: string | null) {
  return useSchreiben<{ id: string; patch: Partial<Klausel> }>(
    ({ id, patch }) => apiPatch(`${BASE}/klauseln/${id}`, patch), werkId);
}

export function useKlauselLoeschen(werkId: string | null) {
  return useSchreiben<string>((id) => apiDelete(`${BASE}/klauseln/${id}`), werkId);
}

export function useOrdnung(werkId: string | null) {
  return useSchreiben<{
    id: string; paragraf: number; sortierung: number;
    dokument?: Blatt; anhang: string | null;
  }[]>(
    (eintraege) => apiPost(`${BASE}/fassungen/${werkId}/ordnung`, { eintraege }), werkId);
}

export function useParagrafAendern(werkId: string | null) {
  return useSchreiben<{
    paragraf: number; anhang: string | null; dokument: Blatt;
    titel?: string; stil?: string | null; stil_setzen?: boolean;
  }>(({ paragraf, ...rest }) =>
    apiPatch(`${BASE}/fassungen/${werkId}/paragrafen/${paragraf}`, rest), werkId);
}

/** Einen ganzen Abschnitt auf ein anderes Blatt schieben — der Kern des
 *  Umbaus vom 20.08.2026. Abschnittsweise und nicht zeilenweise: ein Paragraf,
 *  der halb in der Urkunde und halb in den AGB steht, ist an beiden Stellen
 *  unvollständig, und beide Hälften begännen bei Absatz (1). */
export function useAbschnittVerschieben(werkId: string | null) {
  return useSchreiben<{ dokument: Blatt; paragraf: number; ziel: Blatt }>(
    ({ paragraf, ...rest }) =>
      apiPost(`${BASE}/fassungen/${werkId}/paragrafen/${paragraf}/verschieben`, rest),
    werkId);
}

export function usePruefen(werkId: string | null) {
  return useMutation<{ in_ordnung: boolean; fehler: string[] }>({
    mutationFn: () => apiPost(`${BASE}/fassungen/${werkId}/pruefen`, {}),
  });
}

export type VorschauSchalter = {
  vertragsart: string;
  zahlungsart: string;
  je_einsatz: boolean;
  zahlweise_offen: boolean;
  ohne_zugang: boolean;
  ohne_sondervereinbarung: boolean;
  ohne_bestand: boolean;
  ohne_abruf: boolean;
};

export const VORSCHAU_START: VorschauSchalter = {
  vertragsart: "verbraucher",
  zahlungsart: "ueberweisung",
  je_einsatz: false,
  zahlweise_offen: false,
  ohne_zugang: false,
  ohne_sondervereinbarung: false,
  ohne_bestand: false,
  ohne_abruf: false,
};

export function useVorschau(werkId: string | null, schalter: VorschauSchalter) {
  return useQuery<Vorschau>({
    queryKey: [...SCHLUESSEL, "vorschau", werkId, schalter],
    queryFn: () => apiPost(`${BASE}/fassungen/${werkId}/vorschau`, schalter),
    enabled: !!werkId,
    // Beim Umschalten der Variante soll der alte Text stehen bleiben, statt
    // kurz zu verschwinden — sonst springt die Seite bei jedem Klick.
    placeholderData: (vorher) => vorher,
    retry: false,
  });
}

/* ── Vorschau eines echten Vertrages ─────────────────────────────────────
 *
 * Die Vorschau oben zeigt den Klauselwerk-Text am Beispielkunden — sie
 * beantwortet „stimmt der Text". Diese hier zeigt einen echten Vertrag und
 * beantwortet „was sieht dieser Kunde". Sie ersetzt das PDF aus dem Büro: ein
 * PDF muss hier niemand erzeugen, der Kunde bekommt seines über das Onboarding.
 *
 * Die Entscheidungen, die der Kunde treffen wird, lassen sich durchspielen,
 * ohne sie zu speichern. Genau dafür ist sie da: nicht den einen gespeicherten
 * Stand ansehen, sondern jeden, den er wählen kann.
 */

export type VertragsSchalter = {
  /** `null` heißt „wie gespeichert". */
  zahlweise: string | null;
  zahlungsart: string | null;
  zahlweise_offen: boolean;
  /** IDs der optionalen Leistungen, die als beauftragt gelten. `null` = wie gespeichert. */
  beauftragt: string[] | null;
  /** Testweise unter einer anderen Textfassung ansehen. `null` = die des Vertrages. */
  werk_id: string | null;
};

export const VERTRAGS_VORSCHAU_START: VertragsSchalter = {
  zahlweise: null,
  zahlungsart: null,
  zahlweise_offen: false,
  beauftragt: null,
  werk_id: null,
};

/** Die Zahlweisen, die Vimaana heute anbieten kann.
 *
 *  Lastschrift fehlt mit Absicht: der Einzug ist nicht eingerichtet. Ein
 *  Schalter, der eine Zahlungsart zeigt, die niemand bekommt, führt zu einem
 *  Vertrag, der sie verspricht. */
export const ZAHLWEISE_WAHL: { wert: string; text: string }[] = [
  { wert: "", text: "noch offen — Kunde wählt" },
  { wert: "je_einsatz", text: "nach jedem Einsatz" },
  { wert: "quartalsweise", text: "quartalsweise" },
  { wert: "monatlich", text: "monatlich" },
  { wert: "jaehrlich", text: "jährlich im Voraus" },
];

export type VertragsVorschau = Vorschau & {
  optionale: { id: string; titel: string; beauftragt: boolean }[];
  jahres_netto: string | null;
};

export function useVertragsVorschau(vertragId: string | null, schalter: VertragsSchalter) {
  return useQuery<VertragsVorschau>({
    queryKey: [...SCHLUESSEL, "vertragsvorschau", vertragId, schalter],
    queryFn: () => apiPost(`/api/pflege/vertraege/${vertragId}/vorschau`, schalter),
    enabled: !!vertragId,
    // Beim Umschalten soll der alte Text stehen bleiben, statt kurz zu
    // verschwinden — sonst springt die Seite bei jedem Klick.
    placeholderData: (vorher) => vorher,
    retry: false,
  });
}

/** Paragrafen aus der flachen Klauselliste — die Gliederung, die der Server
 *  beim Rendern erzeugt, hier nur nachgezeichnet. Die gedruckte Nummer entsteht
 *  aus der Position, nicht aus dem Feld `paragraf`: dort dürfen Lücken sein. */
export type ParagrafGruppe = {
  dokument: Blatt;
  paragraf: number;
  anhang: string | null;
  titel: string;
  stil: string | null;
  /** Die gedruckte Nummer — null, wo nicht nummeriert wird. */
  nummer: number | null;
  /** Wie sie gedruckt wird: „Ziffer 3", „§ 3" oder gar nicht. */
  bezeichnung: string | null;
  klauseln: Klausel[];
};

/** Reihenfolge der Blätter — dieselbe wie in `vertrag_render.BLAETTER`. */
export const BLAETTER: Blatt[] = ["urkunde", "anlage", "mandat", "agb", "bestand"];

export const BLATT_LABEL: Record<Blatt, string> = {
  urkunde: "Vertragsurkunde",
  anlage: "Anlage 1 — Einsatzplan und Leistungen",
  mandat: "Anlage 2 — SEPA-Lastschriftmandat",
  agb: "Allgemeine Geschäftsbedingungen",
  bestand: "Bestandsaufnahme",
};

/**
 * Wie die Abschnitte je Blatt heißen — dieselbe Ableitung wie in
 * `vertrag_render.zaehlung`, und aus demselben Grund abgeleitet statt
 * eingestellt: Ist die Fassung aufgeteilt, zählt die Urkunde in Ziffern und
 * die AGB in Paragrafen; ist sie es nicht (Bestand von vor Migration 115),
 * bleibt es überall beim Paragrafen. Ein Editor, der hier anders rechnet als
 * der Renderer, zeigt Nummern, die auf dem Papier nicht stehen.
 */
export function zaehlung(klauseln: Klausel[]): Record<string, string> {
  const aufgeteilt = klauseln.some(k => k.dokument === "agb");
  return { urkunde: aufgeteilt ? "Ziffer" : "§", agb: "§" };
}

export function gliederung(klauseln: Klausel[]): ParagrafGruppe[] {
  const wie = zaehlung(klauseln);
  const rang = (k: Klausel) => BLAETTER.indexOf(k.dokument || "urkunde");
  const sortiert = [...klauseln].sort((a, b) =>
    rang(a) - rang(b) ||
    (a.anhang || "").localeCompare(b.anhang || "") ||
    a.paragraf - b.paragraf ||
    a.sortierung - b.sortierung);

  const gruppen: ParagrafGruppe[] = [];
  for (const k of sortiert) {
    const dokument = (k.dokument || "urkunde") as Blatt;
    const letzte = gruppen[gruppen.length - 1];
    if (letzte && letzte.dokument === dokument
        && letzte.paragraf === k.paragraf && letzte.anhang === (k.anhang || null)) {
      letzte.klauseln.push(k);
      if (!letzte.titel && k.paragraf_titel) letzte.titel = k.paragraf_titel;
      if (k.paragraf_stil) letzte.stil = k.paragraf_stil;
    } else {
      gruppen.push({
        dokument, paragraf: k.paragraf, anhang: k.anhang || null,
        titel: k.paragraf_titel || "", stil: k.paragraf_stil,
        nummer: null, bezeichnung: null, klauseln: [k],
      });
    }
  }

  // Jedes Blatt zählt für sich: Ziffer 1 der Urkunde und § 1 der AGB laufen
  // unabhängig hoch. Altanhänge und die Bestandsaufnahme tragen keine Nummer.
  const zaehler: Record<string, number> = {};
  for (const g of gruppen) {
    if (g.anhang || !wie[g.dokument]) continue;
    zaehler[g.dokument] = (zaehler[g.dokument] || 0) + 1;
    g.nummer = zaehler[g.dokument];
    g.bezeichnung = `${wie[g.dokument]} ${g.nummer}`;
  }
  return gruppen;
}

/** Alle Verweismarken der Fassung mit ihrer aktuellen Bezeichnung — die
 *  Palette, aus der ein Querverweis gesetzt wird. Ein Verweis über die
 *  Blattgrenze bekommt den Zusatz erst beim Rendern; hier steht, wo er
 *  hinzeigt, damit man beim Setzen sieht, ob man das Blatt wechselt. */
export function marken(gruppen: ParagrafGruppe[]): {
  schluessel: string; zeigt: string; dokument: Blatt;
}[] {
  const aus: { schluessel: string; zeigt: string; dokument: Blatt }[] = [];
  for (const g of gruppen) {
    if (!g.bezeichnung) continue;
    let absatz = 0;
    for (const k of g.klauseln) {
      if (k.art === "absatz") absatz += 1;
      if (!k.schluessel) continue;
      aus.push({
        schluessel: k.schluessel,
        zeigt: k.art === "absatz" ? `${g.bezeichnung} Abs. ${absatz}` : g.bezeichnung,
        dokument: g.dokument,
      });
    }
  }
  return aus;
}
